import { Button } from '@/components/ui/button'
import PremiumUpsell from 'app/frontend/base/components/PremiumUpsell'
import { FormGroup } from '@/components/ui/form'
import CronField, {
  defaultCronSchedule,
} from 'app/frontend/base/components/form-fields/CronField'
import NumberField from 'app/frontend/base/components/form-fields/NumberField'
import SelectField from 'app/frontend/base/components/form-fields/SelectField'
import TextField from 'app/frontend/base/components/form-fields/TextField'
import { FormStateContext } from 'app/frontend/base/config/context/forms'
import { useTeam } from 'app/frontend/base/config/context/team'
import { useInstances, usePageInfo } from 'app/frontend/base/config/queries'
import { notificationTemplateVisibleForGameVersion } from 'app/frontend/base/helpers/notificationTemplateVisibility'
import { notificationCtaTargets } from 'app/frontend/types/custom/NotificationTemplateCtaTarget'
import { notificationDeliveryMethods } from 'app/frontend/types/custom/NotificationTemplateMethod'
import { Trash2 } from 'lucide-react'
import { Fragment, useContext, useEffect, useMemo } from 'react'
import { useFormContext, useFieldArray } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
interface Props {
  typeOptions: wowaudit.NotificationTemplateOption[]
}

const NotificationFormContent = ({ typeOptions }: Props) => {
  const { i18n, t } = useTranslation()
  const { guild, team } = useTeam()
  const { handleSectionChange } = useContext(FormStateContext)
  const { watch, setValue, reset } = useFormContext()
  const {
    data: { gameVersion },
  } = usePageInfo()

  const { data: instances } = useInstances()
  const instanceOptions = useMemo(() => {
    return (
      instances?.map((instance) => ({
        label: instance.name,
        value: instance.id,
      })) ?? []
    )
  }, [instances])

  const localizedTypeOptions = useMemo(() => {
    return typeOptions
      .filter((option) =>
        notificationTemplateVisibleForGameVersion(option.key, gameVersion),
      )
      .map((option) => ({
        label: t(`notifications.types.${option.key}.label`),
        value: option.className,
        category: option.category,
        premium: option.premium,
      }))
  }, [typeOptions, gameVersion])

  const id = watch('id')
  const templateType = watch('type')
  const deliveryMethod = watch('deliveryMethod')
  const deliveryTarget = watch('deliveryTarget')
  const ctaTarget = watch('ctaTarget')
  const metadata = watch('metadata')

  const templateOption = typeOptions.find(
    (option) => option.className === templateType,
  )
  const deliveryTargetIdDescription = templateOption?.characterScoped
    ? t(
        'notifications.form-fields.notification_template.deliveryTargetId.singleNotificationDescription',
      )
    : undefined

  useEffect(() => {
    if (templateType && templateOption && !id) {
      const defaultValuesPrefix = `notifications.types.${templateOption.key}.default-values`
      reset({
        id,
        type: templateType,
        messageTitle: t(`${defaultValuesPrefix}.messageTitle`),
        messageTemplate: t(`${defaultValuesPrefix}.messageTemplate`),
        ctaLabel: t(`${defaultValuesPrefix}.ctaLabel`),
        cronSchedule: defaultCronSchedule,
        ...templateOption.defaults,
      })
    }
  }, [templateType])

  const {
    fields: criteriaFields,
    append,
    remove,
  } = useFieldArray({
    name: 'criteria',
  })

  const isPremium = guild?.premiumPoints ? guild.premiumPoints >= 5 : false

  return (
    <>
      <FormGroup>
        <SelectField
          name='type'
          disabled={!!id}
          value={templateType}
          options={localizedTypeOptions}
          transformOptions={false}
          setValue={(value) => {
            if (value)
              setValue(`type`, value as typeof templateType, {
                shouldDirty: true,
              })
          }}
        />

        <SelectField
          name='deliveryMethod'
          options={
            templateOption
              ? templateOption.deliveryMethodOptions
              : [...notificationDeliveryMethods]
          }
        />
      </FormGroup>

      {templateOption ? (
        <>
          {templateOption.premium && !isPremium ? (
            <PremiumUpsell
              className='mb-4'
              title={t('notifications.not-premium-title')}
              description={t('notifications.not-premium-description')}
              to={`${team.path}/settings#premium`}
              onNavigate={() => handleSectionChange('premium')}
            />
          ) : null}

          <p className='mb-6'>
            {t(`notifications.types.${templateOption.key}.description`)}
          </p>
          <FormGroup>
            {deliveryMethod && deliveryMethod !== 'custom_webhook' ? (
              <SelectField
                name='deliveryTarget'
                options={templateOption.deliveryTargetOptions.filter(
                  (target) => deliveryMethod !== 'email' || target !== 'team',
                )}
                optionLabelFn={(target) =>
                  t(
                    `notifications.form-fields.notification_template.deliveryTarget.options.${target}`,
                  )
                }
              />
            ) : null}
            {deliveryTarget === 'user' ? (
              <SelectField<wowaudit.UserReference>
                name='deliveryTargetId'
                customDescription={deliveryTargetIdDescription}
                options={
                  team.userReferences?.filter(
                    (ref) => ref.obfuscatedBattletag,
                  ) ?? []
                }
                optionLabelKey='obfuscatedBattletag'
                optionValueKey='id'
              />
            ) : (
              <TextField
                className={
                  deliveryMethod === 'custom_webhook' ||
                  (deliveryTarget === 'team' && deliveryMethod !== 'email')
                    ? 'visible'
                    : 'invisible'
                }
                name='targetAddress'
                disabled={metadata?.author_id !== undefined}
                customDescription={
                  templateOption.className ===
                  'Template::EventInteractiveOverview'
                    ? metadata?.author_id
                      ? 'This value is managed by the Discord bot'
                      : 'Discord channel ID'
                    : deliveryMethod === 'custom_webhook'
                    ? 'Webhook URL'
                    : undefined
                }
              />
            )}
          </FormGroup>
          {deliveryMethod === 'discord_message' && deliveryTarget === 'team' ? (
            <TextField name='jsonData.mention_ids' />
          ) : null}
          {deliveryTarget !== 'team' ? (
            <SelectField<wowaudit.TeamRank>
              name='teamRankId'
              customPlaceholder='Any rank'
              options={team.teamRanks ?? []}
              optionLabelFn={(rank) => (
                <span style={{ color: rank.color ?? undefined }}>
                  {rank.name}
                </span>
              )}
              optionValueKey='id'
              isClearable
              disableSorting
            />
          ) : null}

          {templateOption.onSchedule ? (
            <CronField name='cronSchedule' shortestHoursInterval={8} />
          ) : (
            <NumberField
              name='delayInMinutes'
              customLabel={
                templateOption.relativeTimeBased
                  ? t('notifications.relative-time-based-label')
                  : undefined
              }
              customDescription={
                templateOption.relativeTimeBased
                  ? t('notifications.relative-time-based-description')
                  : undefined
              }
            />
          )}

          {templateOption.hasCooldown ? <NumberField name='cooldown' /> : null}

          {templateOption.criteriaOptions?.fields?.length ||
          templateOption.criteriaOptions?.events?.length ? (
            <h4 className='mt-8 mb-4'>
              {t('notifications.criteria-section-title')}
            </h4>
          ) : null}

          {templateOption.criteriaOptions?.fields?.length ? (
            <>
              {(templateOption.criteriaOptions.multi
                ? criteriaFields
                : [criteriaFields[0] ?? {}]
              ).map((_item: unknown, index: number) => (
                <FormGroup
                  key={index}
                  className={`mb-0 items-center ${
                    templateOption.criteriaOptions.multi
                      ? ''
                      : '[&>div]:flex-[0_0_calc(50%-0.25rem)]'
                  }`}
                  direction={
                    templateOption.criteriaOptions.multi ? 'row' : 'column'
                  }
                >
                  {templateOption.criteriaOptions.fields?.map((optionField) => {
                    const fieldName = `criteria.${index}.${optionField.name}`
                    const translationKey = `notifications.form-fields.notification_template.criteria.sub-fields.${templateOption.key}.${optionField.name}`
                    const labelTranslationKey = `notifications.form-fields.notification_template.criteria.${optionField.name}`

                    const label =
                      templateOption.criteriaOptions.multi &&
                      i18n.exists(labelTranslationKey) ? (
                        <span
                          className={`whitespace-nowrap mx-2 ${
                            templateOption.criteriaOptions.multi ? '' : 'mb-2'
                          }`}
                          key={`${index}.${optionField.name}.label`}
                        >
                          {t(labelTranslationKey)}
                        </span>
                      ) : null

                    if (optionField.options && optionField.type === 'string') {
                      return (
                        <Fragment key={fieldName}>
                          <SelectField<string>
                            name={fieldName}
                            isMulti={optionField.multi}
                            customPlaceholder={
                              templateOption.criteriaOptions.multi
                                ? ''
                                : t(`${translationKey}.placeholder`)
                            }
                            customLabel={
                              templateOption.criteriaOptions.multi
                                ? ''
                                : t(`${translationKey}.label`)
                            }
                            customDescription={
                              templateOption.criteriaOptions.multi
                                ? ''
                                : t(`${translationKey}.description`)
                            }
                            isClearable={optionField.clearable}
                            options={optionField.options}
                            disableSorting
                            layout={
                              templateOption.criteriaOptions.multi
                                ? 'stacked'
                                : 'inline'
                            }
                          />
                          {label}
                        </Fragment>
                      )
                    }

                    if (optionField.options && optionField.type === 'number') {
                      return (
                        <Fragment key={fieldName}>
                          <SelectField<{ label: string; value: number }>
                            name={fieldName}
                            isMulti={optionField.multi}
                            customPlaceholder={
                              templateOption.criteriaOptions.multi
                                ? ''
                                : t(`${translationKey}.placeholder`)
                            }
                            customLabel={
                              templateOption.criteriaOptions.multi
                                ? ''
                                : t(`${translationKey}.label`)
                            }
                            customDescription={
                              templateOption.criteriaOptions.multi
                                ? ''
                                : t(`${translationKey}.description`)
                            }
                            isClearable={optionField.clearable}
                            options={
                              optionField.options.length
                                ? optionField.options.map((option, i) => ({
                                    label: option,
                                    value: i,
                                    sortLabel: '',
                                  }))
                                : instanceOptions
                            }
                            transformOptions={false}
                            disableSorting
                            layout={
                              templateOption.criteriaOptions.multi
                                ? 'stacked'
                                : 'inline'
                            }
                          />
                          {label}
                        </Fragment>
                      )
                    }

                    if (optionField.type === 'number') {
                      return (
                        <Fragment key={fieldName}>
                          <NumberField
                            name={fieldName}
                            customPlaceholder={
                              templateOption.criteriaOptions.multi
                                ? ''
                                : t(`${translationKey}.placeholder`)
                            }
                            customLabel={
                              templateOption.criteriaOptions.multi
                                ? ''
                                : t(`${translationKey}.label`)
                            }
                            customDescription={
                              templateOption.criteriaOptions.multi
                                ? ''
                                : t(`${translationKey}.description`)
                            }
                            layout={
                              templateOption.criteriaOptions.multi
                                ? 'stacked'
                                : 'inline'
                            }
                          />
                          {label}
                        </Fragment>
                      )
                    }

                    return (
                      <Fragment key={fieldName}>
                        <TextField
                          name={fieldName}
                          layout={
                            templateOption.criteriaOptions.multi
                              ? 'stacked'
                              : 'inline'
                          }
                          customPlaceholder={
                            templateOption.criteriaOptions.multi
                              ? ''
                              : t(`${translationKey}.placeholder`)
                          }
                          customLabel={
                            templateOption.criteriaOptions.multi
                              ? ''
                              : t(`${translationKey}.label`)
                          }
                          customDescription={
                            templateOption.criteriaOptions.multi
                              ? ''
                              : t(`${translationKey}.description`)
                          }
                        />
                        {label}
                      </Fragment>
                    )
                  })}
                  {templateOption.criteriaOptions.multi ? (
                    <span className='flex items-center mb-2'>
                      <Button
                        type='button'
                        size='sm'
                        variant={
                          criteriaFields.length <= 1
                            ? 'secondary'
                            : 'destructive'
                        }
                        disabled={criteriaFields.length <= 1}
                        onClick={() => remove(index)}
                      >
                        <Trash2 size={14} />
                      </Button>
                    </span>
                  ) : null}
                </FormGroup>
              ))}

              {templateOption.criteriaOptions.multi ? (
                <div className='flex justify-end'>
                  <Button
                    type='button'
                    size='sm'
                    className='mt-2'
                    variant='outline'
                    onClick={() =>
                      append(
                        Object.fromEntries(
                          templateOption.criteriaOptions?.fields?.map((f) => [
                            f.name,
                            f.type === 'number' ? 0 : '',
                          ]) ?? [],
                        ),
                      )
                    }
                  >
                    {t('notifications.add-criteria')}
                  </Button>
                </div>
              ) : null}
            </>
          ) : null}

          {templateOption.criteriaOptions?.events?.length ? (
            <SelectField
              name='criteria'
              isMulti={true}
              options={templateOption.criteriaOptions.events}
              disableSorting
            />
          ) : null}

          <h4 className='mt-8 mb-4'>
            {t('notifications.content-section-title')}
          </h4>

          <TextField name='messageTitle' />

          {templateOption.editableTemplate ? (
            <TextField
              isMulti={true}
              name='messageTemplate'
              resetValue={t(
                `notifications.types.${templateOption.key}.default-values.messageTemplate`,
              )}
            />
          ) : null}

          <FormGroup>
            <SelectField
              name='ctaTarget'
              options={[...notificationCtaTargets]}
            />
            <TextField
              className={ctaTarget !== 'none' ? 'visible' : 'invisible'}
              name='ctaLabel'
            />
            <TextField
              name='customCtaUrl'
              className={ctaTarget === 'custom_url' ? 'visible' : 'invisible'}
            />
          </FormGroup>
        </>
      ) : null}
    </>
  )
}

export default NotificationFormContent
